import React, { useEffect, useMemo, useState } from "react";
import Layout from "./../../components/Layout/";
import { AgendaPageSkeleton } from "./components/AgendaSkeleton";
import { useAgendaViewport } from "./hooks/useAgendaLayoutMode";
import ModalUpdate from "./../../components/ModalUpdate/";
import ModalSchedule from "./../../components/ModalSchedule";
import api from "./../../services/api";
import refreshToken from "./../../utils/refreshToken";
import { useAuth } from "./../../hooks/auth";
import { useToast } from "./../../hooks/toast";
import { Container } from "./styles";
import OutsideClickHandler from "react-outside-click-handler";
import ModalToDemarcateSchedule from "../../components/ModalToDemarcateSchedule";
import ModalScheduleLockBlocked from "../../components/ModalScheduleLockBlocked";
import ModalScheduleLockNote from "../../components/ModalScheduleLockNote";
import ModalBlockedScheduleSlot from "../../components/ModalBlockedScheduleSlot";
import ScheduleSlotActionMenu, { type ScheduleSlotMenuItem } from "./components/ScheduleSlotActionMenu";
import MobileSlotActionSheet from "./components/mobile/MobileSlotActionSheet";
import { useRefresh } from "../../hooks/refresh";
import { isConsultantFilterOutOfScope, orderConsultantsForViewer } from "./agendaConsultantScope";
import ModalType from "./ModalType";
import ScheduleType from "./ScheduleType";
import AgendaPage from "./components/AgendaPage";
import { AgendaWeatherProvider } from "./context/AgendaWeatherContext";
import { AGENDA_FEATURE_FLAGS } from "./constants/agendaFeatureFlags";
import ScheduleDetailPanelWithWeather from "./components/ScheduleDetailPanelWithWeather";
import type { ScheduleDetailPanelParam } from "./components/ScheduleDetailPanel";
import ModalUpdateUser from "../../components/ModalUpdateUser";
import { formatDateToOption } from "./util";
import { canChangeSlotLock } from "./scheduleSlotLockAccess";
import { buildPendingAgendaWeekSummary } from "./agendaWeekDateRange";
import { useScheduleTeam } from "../../hooks/scheduleTeam";
import { useAgendaPageData } from "./hooks/useAgendaPageData";
import { useConsultantMobileAgenda } from "./hooks/useConsultantMobileAgenda";
import { cn } from "../../utils/cn";
import type { AgendaWeekSummary } from "./agendaTypes";
import type { ScheduleSearchItem } from "../../types/scheduleCard";
import {
  findScheduledSlotsForCoord,
  getVisibleTeamSearchItems,
} from "./scheduleGridVisibility";
import {
  SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE,
  extractApiErrorMessage,
  findScheduledSlotsInSelection,
  isScheduleLockRefusedByPolicy,
  resolveScheduleLockBlockedMessage,
} from "../../utils/scheduleLockPolicy";
import {
  buildSchedulesToLockPayload,
  truncateScheduleNote,
} from "../../utils/scheduleNote";
import { buildScheduleSearchParams } from "../../utils/scheduleSearchParams";

interface IModal {
  modalToOpen: ModalType;
  param: unknown;
}

const INITIAL_MODAL = {
  modalToOpen: ModalType.NONE,
  param: "",
};

const Schedule: React.FC = () => {
  const { access_token, user, dateToExpires, hasAnyPermission } = useAuth();
  const { addToast } = useToast();
  const { refresh, handleRefresh } = useRefresh();
  const { selectedTeamId, usesTeamSchedules } = useScheduleTeam();

  const isConsultant = user?.role === "consultant";
  const canManageSchedules = !isConsultant;
  const canEditOwnSlots = hasAnyPermission(["adm.schedule.update", "adm.schedule.batchUpdate"]);
  const canChangeLock = (selection: string[]) =>
    canChangeSlotLock({ selection, canManageSchedules, canEditOwnSlots, viewerId: user?.id });

  const {
    filters,
    applyFilters,
    dayData,
    weekSummary,
    loadingInitial,
    loadingDay,
    errorMessage,
    retry,
  } = useAgendaPageData();
  const agendaViewport = useAgendaViewport();

  const agendaSchedules = dayData?.schedules ?? [];

  // Consultants see their team (own agenda first); admins see everyone in scope.
  const viewerConsultantId = isConsultant ? user?.id : undefined;
  const agendaConsultants = useMemo(
    () => (dayData ? orderConsultantsForViewer(dayData.consultants, viewerConsultantId) : []),
    [dayData, viewerConsultantId]
  );
  const consultantOptions = useMemo(
    () =>
      dayData
        ? orderConsultantsForViewer(
            dayData.consultant_options ?? dayData.consultants,
            viewerConsultantId
          )
        : [],
    [dayData, viewerConsultantId]
  );

  // A saved consultant filter from another team would leave the agenda empty.
  useEffect(() => {
    if (loadingDay || !isConsultantFilterOutOfScope(filters.consultantId, consultantOptions)) {
      return;
    }
    applyFilters({ consultantId: undefined });
  }, [applyFilters, consultantOptions, filters.consultantId, loadingDay]);

  const cityOptions = useMemo(() => {
    if (!dayData) {
      return [];
    }
    const allowedCities = new Set(["Fortaleza", "São Paulo", "Recife"]);
    return dayData.commercial_region
      .map((region) => region.name)
      .filter((city) => allowedCities.has(city));
  }, [dayData]);

  // Dates show at once; the visit counts fill in when the week summary arrives.
  const weekSummaryFallback = useMemo<AgendaWeekSummary>(
    () => buildPendingAgendaWeekSummary(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dayData?.date]
  );

  const [dateSelected, setDateSelected] = useState<string[]>([]);
  const [initialStatus, setInitialStatus] = useState<number>();
  const [schedule, setSchedule] = useState("");
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const [optionCoordinates, setOptionCoordinates] = useState({ x: 0, y: 0 });
  const [mobileSlotMenu, setMobileSlotMenu] = useState<{
    title: string;
    items: ScheduleSlotMenuItem[];
  } | null>(null);
  const [shouldOpenOption, setShouldOpenOption] = useState({
    shouldOpen: false,
    date: "",
  });
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingRequestToLockDate, setLoadingRequestToLockDate] = useState(false);
  const [loadModal, setLoadModal] = useState<IModal>(INITIAL_MODAL);
  const [lockBlockedModal, setLockBlockedModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  const [lockNoteModalOpen, setLockNoteModalOpen] = useState(false);
  const [pendingLockSelection, setPendingLockSelection] = useState<string[]>([]);
  const [blockedSlotModal, setBlockedSlotModal] = useState({
    open: false,
    scheduleCoord: "",
    formattedDate: "",
  });
  const [loadingPropertyDetails, setLoadingPropertyDetails] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchResults, setSearchResults] = useState<ScheduleSearchItem[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [detailPanel, setDetailPanel] = useState<{
    open: boolean;
    param: ScheduleDetailPanelParam | null;
  }>({ open: false, param: null });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width: 600px)");
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener("change", sync);
    return () => mediaQuery.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    async function fetchSearchResults() {
      if (!debouncedSearch) {
        setLoadingSearch(false);
        setSearchResults([]);
        return;
      }

      try {
        setLoadingSearch(true);

        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get("/api/adm/schedules/search", {
          params: buildScheduleSearchParams({
            search: debouncedSearch,
            page: 1,
            perPage: 10,
            upcomingOnly: true,
            teamId: usesTeamSchedules && selectedTeamId != null ? selectedTeamId : undefined,
          }),
        });

        const items = response.data.data.items ?? [];
        setSearchResults(getVisibleTeamSearchItems(items));
      } catch (err: any) {
        setSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    }

    if (user) {
      fetchSearchResults();
    }
  }, [
    debouncedSearch,
    access_token,
    dateToExpires,
    user,
    usesTeamSchedules,
    selectedTeamId,
  ]);

  function getScheduleMenuX(pageX: number, offset: number) {
    const preferredX = pageX + offset;
    const menuWidth = 300;
    const viewportRight = window.scrollX + window.innerWidth - 12;
    return Math.max(
      window.scrollX + 12,
      Math.min(preferredX, viewportRight - menuWidth)
    );
  }

  const selectDate = async (
    scheduleOption: { id: number; date: string; status: number },
    event: React.MouseEvent
  ) => {
    // Consultants may only open the details menu on scheduled cells.
    if (
      !canManageSchedules &&
      scheduleOption.status !== ScheduleType.SCHEDULED
    ) {
      return;
    }

    setOptionCoordinates((prevCoords) => ({
      ...prevCoords,
      x: getScheduleMenuX(event.pageX, isMobile ? -10 : 10),
      y: event.pageY + (isMobile ? -10 : 10),
    }));

    const selectionIdentifier = scheduleOption.date;

    setSchedule(selectionIdentifier);
    setInitialStatus(scheduleOption.status);

    if (scheduleOption.status === initialStatus) {
      const checkIfAlreadyIsSelect = dateSelected.find(
        (date) => date === selectionIdentifier
      );

      if (!!checkIfAlreadyIsSelect) {
        const removeId = dateSelected.filter((date) => date !== selectionIdentifier);
        return setDateSelected(removeId);
      }

      setDateSelected((prevDatesSelected) => [
        ...prevDatesSelected,
        selectionIdentifier
      ]);
    } else {
      setDateSelected([selectionIdentifier]);
    }

    setShouldOpenOption((prevOption) => ({
      ...prevOption,
      shouldOpen: true,
      date: formatDateToOption(scheduleOption.date),
    }));
  };

  function selectDateMouseOver(
    scheduleOption: string,
    event: React.MouseEvent
  ) {
    if (mouseIsDown) {
      setOptionCoordinates((prevCoords) => ({
        ...prevCoords,
        x: getScheduleMenuX(event.pageX, isMobile ? -10 : 10),
        y: event.pageY + (isMobile ? -10 : 10),
      }));

      if (shouldOpenOption.shouldOpen) {
        setShouldOpenOption((prevOption) => ({
          ...prevOption,
          shouldOpen: false,
        }));
      }

      const checkIfAlreadyIsSelect = dateSelected.find(
        (date) => date === scheduleOption
      );

      if (!!checkIfAlreadyIsSelect) {
        const removeId = dateSelected.filter((date) => date !== scheduleOption);
        return setDateSelected(removeId);
      }

      setDateSelected((prevDatesSelected) => [
        ...prevDatesSelected,
        scheduleOption,
      ]);
    }
  }

  function buildScheduleParam(scheduleItem: any) {
    const rawDate = String(scheduleItem.date);
    const [datePart, timePart] = rawDate.split(" ");

    return `${datePart}|${timePart}|${scheduleItem.consultant_id}`;
  }

  function openVisitDetails(scheduleCoord: string, targetScheduleId?: number) {
    const matching = findScheduledSlotsForCoord(
      agendaSchedules,
      scheduleCoord,
      ScheduleType.SCHEDULED
    );

    setDetailPanel({
      open: true,
      param: {
        scheduleParam: scheduleCoord,
        targetScheduleId:
          targetScheduleId ??
          (matching.length === 1 ? matching[0].id : undefined),
      },
    });
    setSchedule(scheduleCoord);
  }

  function closeVisitDetailPanel() {
    setDetailPanel({ open: false, param: null });
  }

  function handleOpenSearchResult(item: ScheduleSearchItem) {
    openVisitDetails(buildScheduleParam(item), item.id);
    setSearch("");
  }

  async function handleUnlockDate(selectionOverride?: string[]) {
    const selection = selectionOverride ?? dateSelected;
    if (selection.length === 0) {
      return;
    }
    if (!canChangeLock(selection)) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    setLoadingRequest(true);

    try {
      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const response = await api.post("/api/adm/schedules", {
        schedules: [...selection],
      });

      if (!!response.data.success) {
        addToast({
          title:
            selection.length > 1
              ? "Datas desbloqueadas"
              : "Data desbloqueada",
          type: "success",
          description: "",
        });

        handleRefresh();
        setDateSelected([]);
        setBlockedSlotModal({
          open: false,
          scheduleCoord: "",
          formattedDate: "",
        });
      }

      setLoadingRequest(false);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message as string | undefined;
      if (errorMessage) {
        addToast({
          title: errorMessage,
          type: "error",
        });
      }

      setLoadingRequest(false);
    }
  }

  function closeScheduleOptionsMenu() {
    setShouldOpenOption((prev) => ({ ...prev, shouldOpen: false }));
    setDateSelected([]);
  }

  useEffect(() => {
    if (
      !shouldOpenOption.shouldOpen ||
      mouseIsDown ||
      initialStatus !== ScheduleType.LOCKED ||
      !canManageSchedules ||
      dateSelected.length !== 1
    ) {
      return;
    }

    setBlockedSlotModal({
      open: true,
      scheduleCoord: dateSelected[0],
      formattedDate: shouldOpenOption.date,
    });
    setShouldOpenOption((prev) => ({ ...prev, shouldOpen: false }));
  }, [
    shouldOpenOption.shouldOpen,
    shouldOpenOption.date,
    mouseIsDown,
    initialStatus,
    canManageSchedules,
    dateSelected,
  ]);

  function openLockBlockedModal(message: string) {
    closeScheduleOptionsMenu();
    setLockBlockedModal({
      open: true,
      message: message.trim() || SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE,
    });
  }

  function handleOpenLockNoteModal() {
    if (!canManageSchedules) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    const scheduledInSelection = findScheduledSlotsInSelection(
      dateSelected,
      agendaSchedules
    );
    if (scheduledInSelection.length > 0) {
      openLockBlockedModal(SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE);
      return;
    }

    setPendingLockSelection([...dateSelected]);
    closeScheduleOptionsMenu();
    setLockNoteModalOpen(true);
  }

  function handleOpenBulkLock() {
    if (!canManageSchedules) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    const consultantIds = new Set(agendaConsultants.map((consultant) => consultant.id));
    const lockableSelection = agendaSchedules
      .filter(
        (slot) =>
          slot.status === ScheduleType.UNLOCKED &&
          consultantIds.has(slot.consultant_id)
      )
      .map((slot) => buildScheduleParam(slot));

    if (lockableSelection.length === 0) {
      addToast({
        title: "Não há horários livres para bloquear",
        type: "info",
      });
      return;
    }

    setDateSelected(lockableSelection);
    setPendingLockSelection(lockableSelection);
    setShouldOpenOption((previous) => ({ ...previous, shouldOpen: false }));
    setLockNoteModalOpen(true);
  }

  /**
   * Mobile: tapping a free slot opens the same options as the desktop menu. Managers
   * schedule or lock; consultants only lock, and only on their own agenda.
   */
  function handleOpenMobileFreeSlot(coord: string) {
    const items: ScheduleSlotMenuItem[] = [];
    if (canManageSchedules) {
      items.push({
        id: "schedule-visit",
        label: "Agendar visita",
        tone: "primary",
        onClick: () => {
          setSchedule(coord);
          handleOpenModal(ModalType.SCHEDULE, coord);
        },
      });
    }
    if (canChangeLock([coord])) {
      items.push({
        id: "lock-slot",
        label: "Bloquear horário",
        onClick: () => handleRequestLockSlot(coord),
      });
    }
    if (items.length === 0) {
      return;
    }
    setMobileSlotMenu({ title: formatDateToOption(coord), items });
  }

  /** Mobile: lock one free slot (opens the optional note modal). */
  function handleRequestLockSlot(coord: string) {
    if (!canChangeLock([coord])) {
      addToast({ title: "Sem permissão para alterar a agenda", type: "error" });
      return;
    }
    setDateSelected([coord]);
    setPendingLockSelection([coord]);
    setLockNoteModalOpen(true);
  }

  /** Mobile: open the blocked slot modal, which shows the note and unlocks. */
  function handleRequestUnlockSlot(coord: string) {
    if (!canChangeLock([coord])) {
      addToast({ title: "Sem permissão para alterar a agenda", type: "error" });
      return;
    }
    setDateSelected([coord]);
    setBlockedSlotModal({
      open: true,
      scheduleCoord: coord,
      formattedDate: formatDateToOption(coord),
    });
  }

  async function handleLockDate(noteInput = "") {
    const selection = pendingLockSelection.length > 0
      ? pendingLockSelection
      : dateSelected;

    if (selection.length === 0) {
      return;
    }
    if (!canChangeLock(selection)) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    // Revalidate immediately before sending the request. A visit may have been
    // created after the selection/modal was opened; never overwrite it with a
    // lock in that race window.
    const scheduledInSelection = findScheduledSlotsInSelection(
      selection,
      agendaSchedules
    );
    if (scheduledInSelection.length > 0) {
      setLockNoteModalOpen(false);
      openLockBlockedModal(SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE);
      return;
    }

    const note = truncateScheduleNote(noteInput);
    const hasNote = note.length > 0;

    setLoadingRequestToLockDate(true);

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const schedulesToLockPayload = buildSchedulesToLockPayload(
        selection,
        agendaSchedules
      );

      const isSingleUnlockedWithId =
        schedulesToLockPayload.length === 1 &&
        Number.isInteger(schedulesToLockPayload[0].id) &&
        Number(schedulesToLockPayload[0].id) > 0;

      if (isSingleUnlockedWithId) {
        const response = await api.put(
          `/api/adm/schedules/${schedulesToLockPayload[0].id}`,
          {
            schedule: schedulesToLockPayload[0].schedule,
            status: ScheduleType.LOCKED,
            ...(hasNote ? { note } : {}),
          }
        );

        if (!!response.data.success) {
          addToast({ title: "Data bloqueada", type: "success", description: "" });
          handleRefresh();
          setDateSelected([]);
          setPendingLockSelection([]);
          setLockNoteModalOpen(false);
        }
      } else {
        const response = await api.put("/api/adm/schedules/batch-update", {
          schedules: schedulesToLockPayload,
          status: ScheduleType.LOCKED,
          ...(hasNote ? { note } : {}),
        });

        if (!!response.data.success) {
          addToast({
            title:
              schedulesToLockPayload.length > 1
                ? "Datas bloqueadas"
                : "Data bloqueada",
            type: "success",
            description: "",
          });

          handleRefresh();
          setDateSelected([]);
          setPendingLockSelection([]);
          setLockNoteModalOpen(false);
        }
      }

      setLoadingRequestToLockDate(false);
    } catch (err: unknown) {
      if (isScheduleLockRefusedByPolicy(err)) {
        setLockNoteModalOpen(false);
        openLockBlockedModal(resolveScheduleLockBlockedMessage(err));
      } else {
        addToast({
          title:
            extractApiErrorMessage(err) ??
            "Não foi possível bloquear o horário. Tente novamente.",
          type: "error",
        });
      }

      setLoadingRequestToLockDate(false);
    }
  }

  function handleCloseModal() {
    setLoadModal((prevValue) => ({
      ...prevValue,
      ...INITIAL_MODAL,
    }));
  }

  function handleOpenModal(identifier: ModalType, param: unknown) {
    const writeModalTypes = [
      ModalType.SCHEDULE,
      ModalType.UPDATE,
      ModalType.DEMARCATE,
      ModalType.UPDATE_USER,
    ];

    if (!canManageSchedules && writeModalTypes.includes(identifier)) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    setLoadModal((prevValue) => ({
      ...prevValue,
      modalToOpen: identifier,
      param,
    }));
  }

  async function getPropertyIdFromSchedule(scheduleParam: string): Promise<{ propertyId?: number, hasMultipleAppointments: boolean }> {
    setLoadingPropertyDetails(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

      const response = await api.get(`/api/adm/schedules/${encodeURIComponent(scheduleParam)}`);

      const appointments = response.data.data.original.data;

      const appointmentWithProperty = appointments.find((appointment: any) =>
        appointment.property && appointment.property.id
      );

      if (appointmentWithProperty) {
        const propertyId = appointmentWithProperty.property.id;
        return {
          propertyId,
          hasMultipleAppointments: appointments.length > 1
        };
      }

      return { hasMultipleAppointments: appointments.length > 1 };
    } catch (err: any) {
      return { hasMultipleAppointments: false };
    } finally {
      setLoadingPropertyDetails(false);
    }
  }

  const handleNewScheduleSameProperty = async () => {
    if (!canManageSchedules) {
      addToast({
        title: "Sem permissão para alterar a agenda",
        type: "error",
      });
      return;
    }

    setLoadingPropertyDetails(true);
    try {
      const { propertyId, hasMultipleAppointments } = await getPropertyIdFromSchedule(schedule);

      if (propertyId) {
        handleOpenModal(ModalType.SCHEDULE, {
          schedule: schedule,
          propertyId: propertyId,
        });
      } else {
        handleOpenModal(ModalType.SCHEDULE, schedule);
      }
    } catch (err: any) {
      handleOpenModal(ModalType.SCHEDULE, schedule);
    } finally {
      setLoadingPropertyDetails(false);
    }
  };

  const { isConsultantMobileAgenda } = useConsultantMobileAgenda(agendaConsultants);

  const detailPanelNode = detailPanel.open ? (
    <ScheduleDetailPanelWithWeather
      open={detailPanel.open}
      param={detailPanel.param}
      schedules={agendaSchedules}
      consultants={agendaConsultants}
      onClose={closeVisitDetailPanel}
      onDemarcate={(scheduleId, scheduleDate) =>
        handleOpenModal(ModalType.DEMARCATE, {
          scheduleId,
          schedule: scheduleDate,
        })
      }
      onUpdate={(scheduleId, scheduleParam, videoCall) =>
        handleOpenModal(ModalType.UPDATE, {
          scheduleId,
          schedule: scheduleParam,
          consultants: agendaConsultants,
          video_call: videoCall,
        })
      }
      onVisitOutcomeUpdated={retry}
      onScheduleAnother={
        canManageSchedules
          ? (scheduleParam, propertyId) => {
              // Same consultant and hour only for the same property: the modal locks it.
              closeVisitDetailPanel();
              setSchedule(scheduleParam);
              handleOpenModal(
                ModalType.SCHEDULE,
                propertyId ? { schedule: scheduleParam, propertyId } : scheduleParam
              );
            }
          : undefined
      }
    />
  ) : null;

  return (
    <>
      {loadingInitial && !dayData ? (
        <Layout>
          <div
            className={cn(
              "schedule-page-shell mx-auto flex w-full max-w-none flex-1 flex-col overflow-x-hidden bg-white",
              agendaViewport === "mobile" ? "py-2" : "px-3 py-4 sm:px-4 md:py-5"
            )}
          >
            <AgendaPageSkeleton viewport={agendaViewport} />
          </div>
        </Layout>
      ) : (
        <Container
          onMouseUp={() => {
            setMouseIsDown(false);
            setShouldOpenOption((prevOption) => ({
              ...prevOption,
              shouldOpen: true,
            }));
          }}
        >
          <Layout>
            <div
              className={cn(
                "schedule-page-shell mx-auto flex w-full max-w-none flex-1 flex-col overflow-x-hidden bg-white",
                isConsultantMobileAgenda
                  ? "px-0 py-2 sm:px-0 md:py-2"
                  : "px-3 py-4 sm:px-4 md:py-5"
              )}
            >
              <>
                {errorMessage && !dayData ? (
                  <div className="mx-auto mt-8 w-full max-w-lg rounded-md border border-red-200 bg-red-50 px-6 py-10 text-center">
                    <p className="text-base font-medium text-red-700">
                      {errorMessage ||
                        (isConsultantMobileAgenda
                          ? "Não foi possível carregar sua agenda."
                          : "Não foi possível carregar a agenda.")}
                    </p>
                    <button
                      type="button"
                      onClick={retry}
                      className="mt-4 rounded-xl bg-[#dd6435] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c4552b]"
                    >
                      Tentar novamente
                    </button>
                  </div>
                ) : null}

                {dayData ? (
                  <>
                    <OutsideClickHandler
                      onOutsideClick={() =>
                        setDateSelected((prevValue) =>
                          shouldOpenOption.shouldOpen && !mouseIsDown ? [] : [...prevValue]
                        )
                      }
                    >
                      {canManageSchedules &&
                        shouldOpenOption.shouldOpen &&
                        !mouseIsDown &&
                        initialStatus === ScheduleType.LOCKED &&
                        dateSelected.length > 1 && (
                        <ScheduleSlotActionMenu
                          coordinate={optionCoordinates}
                          title={`${dateSelected.length} horários selecionados`}
                          items={[
                            {
                              id: "unlock-batch",
                              label: loadingRequest
                                ? "Carregando..."
                                : "Desbloquear horários",
                              tone: "primary",
                              disabled: loadingRequest,
                              onClick: () => handleUnlockDate(),
                            },
                          ]}
                        />
                      )}

                      {canManageSchedules &&
                        shouldOpenOption.shouldOpen &&
                        !mouseIsDown &&
                        initialStatus === ScheduleType.UNLOCKED &&
                        dateSelected.length > 0 && (
                        <ScheduleSlotActionMenu
                          coordinate={optionCoordinates}
                          title={
                            dateSelected.length === 1
                              ? shouldOpenOption.date
                              : `${dateSelected.length} horários selecionados`
                          }
                          items={[
                            ...(dateSelected.length === 1
                              ? [
                                  {
                                    id: "schedule-visit",
                                    label: "Agendar visita",
                                    tone: "primary" as const,
                                    onClick: () =>
                                      handleOpenModal(ModalType.SCHEDULE, schedule),
                                  },
                                ]
                              : []),
                            {
                              id: "lock-slots",
                              label: "Bloquear horário",
                              onClick: handleOpenLockNoteModal,
                            },
                          ]}
                        />
                      )}

                      {shouldOpenOption.shouldOpen &&
                        !mouseIsDown &&
                        initialStatus === ScheduleType.SCHEDULED &&
                        dateSelected.length > 0 && (
                        <ScheduleSlotActionMenu
                          coordinate={optionCoordinates}
                          title={shouldOpenOption.date}
                          items={[
                            ...(canManageSchedules
                              ? [
                                  {
                                    id: "schedule-again",
                                    label: loadingPropertyDetails
                                      ? "Carregando..."
                                      : "Agendar novamente",
                                    disabled: loadingPropertyDetails,
                                    onClick: handleNewScheduleSameProperty,
                                  },
                                ]
                              : []),
                            {
                              id: "visit-details",
                              label: "Detalhes da visita",
                              tone: "primary",
                              onClick: () => openVisitDetails(schedule),
                            },
                            ...(canManageSchedules
                              ? (() => {
                                  const [datePart, timePart, consultantId] =
                                    schedule.split("|");
                                  const [year, month, day] = datePart.split("-");
                                  const dateStr = `${year}-${month}-${day}`;
                                  const timeStr = `${timePart}:00`;

                                  const appointmentsForThisCell =
                                    agendaSchedules.filter(
                                      (scheduleItem) =>
                                        scheduleItem.date.startsWith(dateStr) &&
                                        scheduleItem.date.includes(timeStr) &&
                                        scheduleItem.consultant_id ===
                                          parseInt(consultantId, 10) &&
                                        scheduleItem.status ===
                                          ScheduleType.SCHEDULED
                                    );

                                  if (appointmentsForThisCell.length !== 1) {
                                    return [];
                                  }

                                  const scheduleId = appointmentsForThisCell[0].id;

                                  return [
                                    {
                                      id: "update-visit",
                                      label: "Alterar agendamento",
                                      onClick: () =>
                                        handleOpenModal(ModalType.UPDATE, {
                                          scheduleId,
                                          schedule,
                                          consultants: agendaConsultants,
                                        }),
                                    },
                                    {
                                      id: "cancel-visit",
                                      label: "Desmarcar visita",
                                      tone: "danger" as const,
                                      onClick: () =>
                                        handleOpenModal(ModalType.DEMARCATE, {
                                          scheduleId,
                                          schedule,
                                        }),
                                    },
                                  ];
                                })()
                              : []),
                          ]}
                        />
                      )}
                    </OutsideClickHandler>

                    {loadModal.modalToOpen === ModalType.UPDATE && (
                      <ModalUpdate
                        open={loadModal.modalToOpen === ModalType.UPDATE}
                        functionToClose={handleCloseModal}
                        param={loadModal.param}
                      />
                    )}

                    {loadModal.modalToOpen === ModalType.SCHEDULE && (
                      <ModalSchedule
                        open={loadModal.modalToOpen === ModalType.SCHEDULE}
                        functionToCloseModalSchedule={handleCloseModal}
                        param={loadModal.param}
                      />
                    )}

                    {loadModal.modalToOpen === ModalType.UPDATE_USER && (
                      <ModalUpdateUser
                        open={loadModal.modalToOpen === ModalType.UPDATE_USER}
                        onReturn={handleOpenModal}
                        param={loadModal.param}
                      />
                    )}

                    <ModalToDemarcateSchedule
                      open={loadModal.modalToOpen === ModalType.DEMARCATE}
                      dateSelected={loadModal.param}
                      functionToCloseModal={handleCloseModal}
                    />

                    <ModalScheduleLockBlocked
                      open={lockBlockedModal.open}
                      message={lockBlockedModal.message}
                      onClose={() =>
                        setLockBlockedModal({ open: false, message: "" })
                      }
                    />

                    <ModalScheduleLockNote
                      open={lockNoteModalOpen}
                      slotCount={pendingLockSelection.length}
                      isLoading={loadingRequestToLockDate}
                      onConfirm={handleLockDate}
                      onClose={() => {
                        if (!loadingRequestToLockDate) {
                          setLockNoteModalOpen(false);
                          setPendingLockSelection([]);
                        }
                      }}
                    />

                    <MobileSlotActionSheet
                      open={mobileSlotMenu != null}
                      title={mobileSlotMenu?.title ?? ""}
                      items={mobileSlotMenu?.items ?? []}
                      onClose={() => setMobileSlotMenu(null)}
                    />

                    <ModalBlockedScheduleSlot
                      open={blockedSlotModal.open}
                      scheduleCoord={blockedSlotModal.scheduleCoord}
                      formattedDate={blockedSlotModal.formattedDate}
                      isUnlocking={loadingRequest}
                      onClose={() => {
                        setBlockedSlotModal({
                          open: false,
                          scheduleCoord: "",
                          formattedDate: "",
                        });
                        setDateSelected([]);
                      }}
                      onUnlock={() =>
                        handleUnlockDate([blockedSlotModal.scheduleCoord])
                      }
                    />

                      <AgendaWeatherProvider
                        enabled={AGENDA_FEATURE_FLAGS.weatherRow}
                        date={filters.date}
                      >
                        <AgendaPage
                          filters={filters}
                          dayData={dayData}
                          weekSummary={weekSummary ?? weekSummaryFallback}
                          loadingDay={loadingDay}
                          consultants={agendaConsultants}
                          consultantOptions={consultantOptions}
                          cityOptions={cityOptions}
                          showCityFilter={!isConsultant}
                          showConsultantFilter={!isConsultant || consultantOptions.length > 1}
                          canManageSchedules={canManageSchedules}
                          search={search}
                          onSearchChange={setSearch}
                          searchLoading={loadingSearch}
                          searchResults={searchResults}
                          onOpenSearchResult={handleOpenSearchResult}
                          onNewSchedule={() => handleOpenModal(ModalType.SCHEDULE, "")}
                          onBulkBlock={handleOpenBulkLock}
                          onUnlockSlot={canManageSchedules || canEditOwnSlots ? handleRequestUnlockSlot : undefined}
                          onApplyFilters={applyFilters}
                          dateSelected={dateSelected}
                          initialStatus={initialStatus}
                          onSelectCell={(scheduleOption, event) => {
                            setInitialStatus(scheduleOption.status);
                            selectDate(scheduleOption, event);
                            setSchedule(scheduleOption.date);
                            setMouseIsDown(true);
                          }}
                          onSelectCellMouseOver={selectDateMouseOver}
                          onOpenVisitDetails={openVisitDetails}
                          onScheduleFreeSlot={handleOpenMobileFreeSlot}
                          onVisitOutcomeUpdated={retry}
                          detailPanel={detailPanelNode}
                        />
                      </AgendaWeatherProvider>
                  </>
                ) : null}
              </>
            </div>
          </Layout>
        </Container>
      )}
    </>
  );
};

export default Schedule;
